import { displayToastError, http } from 'app';

interface CatiaRegion {
  code: string;
  displayName: string;
  countryCode: string;
  active: boolean;
}

type ProcessFileAndGetAnomaliesResult =
  | { kind: 'file'; file: Blob; fileName: string }
  | { kind: 'no-anomalies'; message: string };

const DEFAULT_FILE_NAME = 'catastrophe_detection_results.xlsx';

const parseBlobJson = async (
  data: unknown
): Promise<{
  hasAnomalies?: boolean;
  message?: string;
  detail?: string;
} | null> => {
  if (!(data instanceof Blob)) return null;

  try {
    return JSON.parse(await data.text()) as {
      hasAnomalies?: boolean;
      message?: string;
      detail?: string;
    };
  } catch {
    return null;
  }
};

export const uploadCatiaFile = async (
  businessArea: string,
  service: string,
  region: string,
  body: any
): Promise<string> => {
  try {
    const response = await http.post(
      `/${businessArea}/${service}/${region}/dataset`,
      body
    );
    return response.data as string;
  } catch (error: any) {
    const errorMessage = error.response?.data?.message ?? error.message;
    displayToastError(String(errorMessage), 'Upload CATIA dataset');
    throw error;
  }
};

export const getAnomalies = async (
  businessArea: string,
  service: string,
  region: string,
  datasetId: string,
  keywords: string,
  antiKeywords: string
): Promise<any> => {
  try {
    const response = await http.get(
      `/${businessArea}/${service}/${region}/dataset/${datasetId}/anomalies?keywords=${keywords}&anitKeywords=${antiKeywords}`
    );
    return response.data;
  } catch (error: any) {
    const errorMessage = error.response?.data?.message ?? error.message;
    displayToastError(String(errorMessage), 'Get CATIA anomalies');
    throw error;
  }
};

export const getCatiaRegions = async (
  businessArea: string,
  service: string
): Promise<CatiaRegion[]> => {
  try {
    const response = await http.get(`/${businessArea}/${service}/bootstrap`);
    const data = response.data as { regions?: CatiaRegion[] };
    return data.regions ?? [];
  } catch (error: any) {
    const errorMessage = error.response?.data?.message ?? error.message;
    displayToastError(String(errorMessage), 'Get CATIA regions');
    return [];
  }
};

export const processFileAndGetAnomalies = async (
  businessArea: string,
  service: string,
  region: string,
  file: File,
  keywords?: string,
  antiKeywords?: string
): Promise<ProcessFileAndGetAnomaliesResult> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const params = new URLSearchParams();
    if (keywords) params.append('keywords', keywords);
    if (antiKeywords) params.append('antiKeywords', antiKeywords);

    const queryString = params.toString();
    const url = `/${businessArea}/${service}/${region}/process-file${
      queryString ? `?${queryString}` : ''
    }`;

    const response = await http.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      responseType: 'blob'
    });

    const contentType = String(response.headers['content-type'] ?? '');
    if (contentType.includes('application/json')) {
      const successPayload = await parseBlobJson(response.data);
      if (successPayload?.hasAnomalies === false) {
        const message = successPayload.message ?? successPayload.detail;
        return {
          kind: 'no-anomalies',
          message: String(message ?? '')
        };
      }
    }

    const contentDisposition = response?.headers['content-disposition'];
    const matches = contentDisposition?.includes('filename=')
      ? contentDisposition.match(/filename="([^"]+)"/)
      : null;
    const fileName = matches?.[1] ?? DEFAULT_FILE_NAME;

    return {
      kind: 'file',
      file: response.data as Blob,
      fileName
    };
  } catch (error: any) {
    const errorPayload = await parseBlobJson(error.response?.data);
    const errorMessage =
      errorPayload?.message ??
      errorPayload?.detail ??
      error.response?.data?.message ??
      error.message;

    displayToastError(errorMessage, 'Process file and get anomalies');
    throw new Error(errorMessage);
  }
};
